/**
 * Case Detail View
 * Display complete case information
 */

import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Briefcase, FileText, Gavel, User } from 'lucide-react'

import { useCase } from '@/hooks/useCases'
import { PageLoading } from '@/components/loading'
import { Button } from '@/components/ui/button'
import { CASE_STATUS_LABELS, CASE_STATUS_COLORS } from '@/types/case'

export function CaseDetail() {
  const { id } = useParams<{ id: string }>()

  // Fetch case data
  const { data: caseData, isLoading, error } = useCase(id!)

  // Format JSON for display
  const formatJSON = (data: any): string => {
    if (!data) return 'N/A'
    try {
      return JSON.stringify(data, null, 2)
    } catch {
      return 'Invalid JSON'
    }
  }

  if (isLoading) {
    return <PageLoading message="Loading case details..." />
  }

  if (error || !caseData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-lg text-red-600">Case not found</p>
          <Link to="/cases">
            <Button className="mt-4 bg-primary">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Cases
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/cases">
            <Button variant="outline" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-primary">
              {caseData.title || 'Untitled Case'}
            </h1>
            <p className="text-gray-600 mt-1">Case ID: {caseData.id}</p>
          </div>
        </div>
        {/* Edit button hidden for now */}
      </div>

      {/* Basic Information */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <Briefcase className="h-5 w-5" />
          Basic Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoItem label="Case Title" value={caseData.title} />
          <InfoItem label="Case Number" value={caseData.case_number} />
          <InfoItem label="Case Type" value={caseData.case_type} />
          <InfoItem label="Status">
            {caseData.status ? (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CASE_STATUS_COLORS[caseData.status]}`}>
                {CASE_STATUS_LABELS[caseData.status]}
              </span>
            ) : (
              <span className="text-gray-400">N/A</span>
            )}
          </InfoItem>
          <InfoItem label="Nature of Case" value={caseData.nature} className="md:col-span-2" />
        </div>
      </div>

      {/* Court Information */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <Gavel className="h-5 w-5" />
          Court Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoItem label="Court Pending" value={caseData.court_pending} />
          <InfoItem label="Court Code" value={caseData.court_code} />
          <InfoItem label="Place of Detention" value={caseData.place_of_detention} className="md:col-span-2" />
        </div>
      </div>

      {/* Case Details */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5" />
          Case Details
        </h2>
        <div className="space-y-4">
          <InfoItem label="Action Taken" value={caseData.action_taken} />

          {/* Adverse Information */}
          {caseData.adverse && (
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">Adverse Information</p>
              <pre className="mt-1 p-3 bg-gray-50 border rounded text-sm overflow-x-auto">
                {formatJSON(caseData.adverse)}
              </pre>
            </div>
          )}

          {/* Other Details */}
          {caseData.other_details && (
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">Other Details</p>
              <pre className="mt-1 p-3 bg-gray-50 border rounded text-sm overflow-x-auto">
                {formatJSON(caseData.other_details)}
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Information */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-xl font-semibold text-primary flex items-center gap-2 mb-4">
          <User className="h-5 w-5" />
          Assignment Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoItem label="Assigned Lawyer">
            {caseData.lawyer_firstname || caseData.lawyer_lastname
              ? `Atty. ${caseData.lawyer_firstname || ''} ${caseData.lawyer_lastname || ''}`.trim()
              : caseData.lawyer_username || (
                  <span className="text-gray-400">Not Assigned</span>
                )}
          </InfoItem>
          <InfoItem label="Originating Visit">
            {caseData.visit_id ? (
              <Link to={`/visits/${caseData.visit_id}`} className="text-primary hover:underline">
                View Visit
              </Link>
            ) : (
              <span className="text-gray-400">N/A</span>
            )}
          </InfoItem>
        </div>
      </div>
    </div>
  )
}

// Helper component for displaying info items
function InfoItem({
  label,
  value,
  children,
  className = ''
}: {
  label: string
  value?: any
  children?: React.ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <div className="mt-1 text-base text-gray-900">
        {children || value || 'N/A'}
      </div>
    </div>
  )
}

export default CaseDetail
